import React, { useState, useEffect, useCallback } from 'react';
import GameHUD from '../shared/GameHUD';
import QuestionOverlay from '../shared/QuestionOverlay';
import PauseMenu from '../shared/PauseMenu';
import GameComplete from '../GameComplete';
import { shuffleArray, calculateRewards } from '../../../services/gameService';
import './Match3Game.css';

function Match3Game({
    gameContent,
    template,
    topic,
    difficulty = 'intermediate',
    onComplete,
    onQuit
}) {
    // Game state
    const [tiles, setTiles] = useState([]);
    const [selectedTile, setSelectedTile] = useState(null);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [score, setScore] = useState(0);
    const [combo, setCombo] = useState(0);
    const [bestCombo, setBestCombo] = useState(0);
    const [lives, setLives] = useState(3);
    const [timeLeft, setTimeLeft] = useState(180);
    const [xpEarned, setXpEarned] = useState(0);
    const [coinsEarned, setCoinsEarned] = useState(0);

    // UI state
    const [isPaused, setIsPaused] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [victory, setVictory] = useState(false);
    const [showQuestion, setShowQuestion] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [matchAnimation, setMatchAnimation] = useState(null);

    // Initialize game
    useEffect(() => {
        if (gameContent?.tiles) {
            const shuffledTiles = shuffleArray(gameContent.tiles).map((tile, index) => ({
                ...tile,
                index,
                isFlipped: false,
                isMatched: false
            }));
            setTiles(shuffledTiles);
        }

        // Set difficulty-based settings
        const diffSettings = template?.difficultyScaling?.[difficulty] || {};
        setLives(diffSettings.lives || 3);
        setTimeLeft(diffSettings.timeLimit || 180);
    }, [gameContent, template, difficulty]);

    const handleGameOver = useCallback((won) => {
        setVictory(won);
        setIsComplete(true);

        const accuracy = tiles.length > 0
            ? Math.round((matchedPairs.length / (tiles.length / 2)) * 100)
            : 0;

        const timeBonus = won ? Math.floor(timeLeft / 10) * 5 : 0;
        const rewards = calculateRewards(template, matchedPairs.length, accuracy, timeBonus);

        setXpEarned(rewards.xp);
        setCoinsEarned(rewards.coins);

        if (onComplete) {
            onComplete({
                victory: won,
                score,
                xp: rewards.xp,
                coins: rewards.coins,
                stars: rewards.stars,
                accuracy,
                stats: {
                    matches: matchedPairs.length,
                    bestCombo,
                    timeElapsed: (template?.difficultyScaling?.[difficulty]?.timeLimit || 180) - timeLeft
                }
            });
        }
    }, [score, matchedPairs, tiles.length, timeLeft, bestCombo, template, difficulty, onComplete]);

    // Timer
    useEffect(() => {
        if (isPaused || isComplete || showQuestion) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleGameOver(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isPaused, isComplete, showQuestion, handleGameOver]);

    // Check for game completion
    useEffect(() => {
        if (tiles.length > 0 && matchedPairs.length === tiles.length / 2) {
            handleGameOver(true);
        }
    }, [matchedPairs, tiles.length, handleGameOver]);

    const handleTileClick = (clickedTile) => {
        if (isPaused || isComplete || isAnimating || clickedTile.isMatched) return;

        // If clicking the same tile, deselect it
        if (selectedTile?.id === clickedTile.id) {
            setSelectedTile(null);
            return;
        }

        // If no tile selected, select this one
        if (!selectedTile) {
            setSelectedTile(clickedTile);
            return;
        }

        // Check for match
        setIsAnimating(true);

        if (selectedTile.pairId === clickedTile.pairId) {
            // Match found!
            setMatchAnimation({ tile1: selectedTile.id, tile2: clickedTile.id });

            setTimeout(() => {
                setTiles(prev => prev.map(tile =>
                    tile.pairId === clickedTile.pairId
                        ? { ...tile, isMatched: true }
                        : tile
                ));

                setMatchedPairs(prev => [...prev, clickedTile.pairId]);
                setScore(prev => prev + 100 + (combo * 25));
                setCombo(prev => {
                    const newCombo = prev + 1;
                    if (newCombo > bestCombo) setBestCombo(newCombo);
                    return newCombo;
                });

                // Maybe show bonus question
                if (gameContent?.bonusQuestions && Math.random() > 0.6) {
                    const nextQuestion = gameContent.bonusQuestions[questionIndex % gameContent.bonusQuestions.length];
                    if (nextQuestion) {
                        setCurrentQuestion(nextQuestion);
                        setShowQuestion(true);
                        setQuestionIndex(prev => prev + 1);
                    }
                }

                setMatchAnimation(null);
                setSelectedTile(null);
                setIsAnimating(false);
            }, 600);
        } else {
            // No match
            setMatchAnimation({ tile1: selectedTile.id, tile2: clickedTile.id, failed: true });

            setTimeout(() => {
                setCombo(0);
                setLives(prev => {
                    const newLives = prev - 1;
                    if (newLives <= 0) {
                        handleGameOver(false);
                    }
                    return newLives;
                });

                setMatchAnimation(null);
                setSelectedTile(null);
                setIsAnimating(false);
            }, 800);
        }
    };

    const handleQuestionAnswer = (correct, xpBonus) => {
        if (correct) {
            setScore(prev => prev + 50);
            setXpEarned(prev => prev + xpBonus);
        }
        setShowQuestion(false);
        setCurrentQuestion(null);
    };

    const handleRestart = () => {
        const shuffledTiles = shuffleArray(gameContent.tiles).map((tile, index) => ({
            ...tile,
            index,
            isFlipped: false,
            isMatched: false
        }));

        setTiles(shuffledTiles);
        setSelectedTile(null);
        setMatchedPairs([]);
        setScore(0);
        setCombo(0);
        setBestCombo(0);
        setLives(template?.difficultyScaling?.[difficulty]?.lives || 3);
        setTimeLeft(template?.difficultyScaling?.[difficulty]?.timeLimit || 180);
        setXpEarned(0);
        setCoinsEarned(0);
        setIsPaused(false);
        setIsComplete(false);
        setVictory(false);
        setQuestionIndex(0);
    };

    const getTileClass = (tile) => {
        let classes = ['match3-tile'];

        if (tile.isMatched) classes.push('matched');
        if (selectedTile?.id === tile.id) classes.push('selected');
        if (tile.type === 'concept') classes.push('concept');
        if (tile.type === 'definition') classes.push('definition');

        if (matchAnimation) {
            if (matchAnimation.tile1 === tile.id || matchAnimation.tile2 === tile.id) {
                classes.push(matchAnimation.failed ? 'shake' : 'match-success');
            }
        }

        return classes.join(' ');
    };

    if (!gameContent) {
        return (
            <div className="match3-loading">
                <div className="loading-spinner">🎮</div>
                <p>Loading game...</p>
            </div>
        );
    }

    return (
        <div className="match3-game">
            <GameHUD
                gameName="Memory Match"
                score={score}
                lives={lives}
                maxLives={template?.difficultyScaling?.[difficulty]?.lives || 3}
                timeLeft={timeLeft}
                combo={combo}
                xp={xpEarned}
                coins={coinsEarned}
                onPause={() => setIsPaused(true)}
            />

            <div className="match3-info">
                <span className="match3-topic">📚 {topic}</span>
                <span className="match3-progress">
                    {matchedPairs.length} / {tiles.length / 2} pairs matched
                </span>
            </div>

            <div className="match3-board">
                <div className="tiles-grid">
                    {tiles.map((tile) => (
                        <button
                            key={tile.id}
                            className={getTileClass(tile)}
                            onClick={() => handleTileClick(tile)}
                            disabled={tile.isMatched || isAnimating}
                        >
                            <div className="tile-content">
                                <span className="tile-type-indicator">
                                    {tile.type === 'concept' ? '💡' : '📖'}
                                </span>
                                <span className="tile-text">{tile.content}</span>
                            </div>
                            {tile.isMatched && (
                                <div className="tile-matched-overlay">
                                    <span>✓</span>
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div className="match3-hint">
                💡 Match concepts with their definitions!
            </div>

            {/* Pause Menu */}
            {isPaused && (
                <PauseMenu
                    gameName="Memory Match"
                    score={score}
                    stats={{
                        matches: matchedPairs.length,
                        combo: bestCombo,
                        accuracy: tiles.length > 0
                            ? Math.round((matchedPairs.length / (tiles.length / 2)) * 100)
                            : 0
                    }}
                    onResume={() => setIsPaused(false)}
                    onRestart={handleRestart}
                    onQuit={onQuit}
                />
            )}

            {/* Question Overlay */}
            {showQuestion && currentQuestion && (
                <QuestionOverlay
                    question={currentQuestion.question}
                    options={currentQuestion.options}
                    correctAnswer={currentQuestion.correctAnswer}
                    explanation={currentQuestion.explanation}
                    xpBonus={currentQuestion.xpBonus || 15}
                    onAnswer={handleQuestionAnswer}
                />
            )}

            {/* Game Complete */}
            {isComplete && (
                <GameComplete
                    victory={victory}
                    gameName="Memory Match"
                    score={score}
                    xpEarned={xpEarned}
                    coinsEarned={coinsEarned}
                    stars={victory ? (matchedPairs.length === tiles.length / 2 ? 3 : 2) : 1}
                    accuracy={tiles.length > 0
                        ? Math.round((matchedPairs.length / (tiles.length / 2)) * 100)
                        : 0}
                    stats={{
                        matches: matchedPairs.length,
                        bestCombo,
                        timeElapsed: (template?.difficultyScaling?.[difficulty]?.timeLimit || 180) - timeLeft
                    }}
                    onPlayAgain={handleRestart}
                    onBackToMap={onQuit}
                    topic={topic}
                />
            )}
        </div>
    );
}

export default Match3Game;
